import * as THREE from 'three';
import { bakeStatic, unifyMaterials } from './bake.js';

/**
 * The club: a low-poly driver/iron/putter that swings in time with the meter.
 *
 * There is no golfer — just the club, pivoting about where the hands would
 * be. It is driven entirely by one angle, theta (0 = address, negative =
 * backswing, positive = follow-through), so the swing on screen IS the meter:
 * the head meets the ball at the exact moment the marker meets the line.
 */

export const HANDS_UP = 1.25;    // pivot height above the ball

// One size of club, and so one size of golfer, for everyone on the course:
// you, your friends and the bots stand at the same scale
export const CAMPER_CLUB_SIZE = 0.75;
export const CAMPER_SCALE = (HANDS_UP * CAMPER_CLUB_SIZE - 0.05) / 0.43;
const HANDS_IN = 0.55;    // pivot offset toward the (right-handed) golfer

const LIE = Math.atan2(HANDS_IN, HANDS_UP); // shaft lean away from vertical
const HEAD_BACK = 0.1;                      // head sits this far behind the ball

/** A hosel: a short sleeve lying exactly along the shaft where it meets the head. */
function hosel(mat, radius, len) {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.85, radius, len, 8), mat);
  mesh.rotation.x = -LIE;
  mesh.position.set(0, Math.cos(LIE) * len * 0.5, -Math.sin(LIE) * len * 0.5);
  return mesh;
}

/**
 * Club heads are modelled in "head space": +X is the way the face points,
 * +Y is up, +Z runs from heel to toe, and the origin is the heel on the
 * ground — where the shaft comes in.
 */
function buildWood(mat) {
  const group = new THREE.Group();
  // Pear-shaped crown: a squashed sphere with the front sliced flat for a face
  const geo = new THREE.SphereGeometry(1, 14, 10);
  geo.scale(0.15, 0.085, 0.16);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i);
    if (x > 0.075) pos.setX(i, 0.075);
    // Taper the back toward the heel so it reads as a driver, not a ball
    if (x < 0) pos.setZ(i, z * (1 + x * 1.6));
    if (pos.getY(i) < -0.05) pos.setY(i, -0.05);
  }
  geo.computeVertexNormals();
  const body = new THREE.Mesh(geo, mat.crown);
  body.position.set(-0.075, 0.062, 0.15);
  const face = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.085, 0.23), mat.face);
  face.position.set(0.004, 0.058, 0.15);
  const sole = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.014, 0.2), mat.steel);
  sole.position.set(-0.09, 0.012, 0.15);
  const mark = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.006, 0.014), mat.accent);
  mark.position.set(-0.03, 0.146, 0.15);
  // Shaft enters a third of the way back from the face
  for (const part of [body, face, sole, mark]) part.position.x += 0.035;
  group.add(body, face, sole, mark, hosel(mat.crown, 0.03, 0.2));
  return group;
}

function buildIron(mat) {
  const group = new THREE.Group();
  // The classic blade outline: low at the heel, tall at the toe
  const outline = new THREE.Shape();
  outline.moveTo(0.0, 0.0);
  outline.lineTo(0.2, 0.0);
  outline.quadraticCurveTo(0.25, 0.02, 0.24, 0.1);
  outline.lineTo(0.2, 0.145);
  outline.lineTo(0.03, 0.07);
  outline.lineTo(0.0, 0.0);
  const blade = new THREE.ExtrudeGeometry(outline, { depth: 0.035, bevelEnabled: false });
  blade.rotateY(-Math.PI / 2);   // outline now runs heel->toe along Z, thickness back along -X
  blade.rotateZ(0.42);           // loft: the top edge leans back
  const body = new THREE.Mesh(blade, mat.steel);
  // Dark grooves so the face reads as a face
  const grooves = new THREE.Group();
  for (let i = 0; i < 4; i++) {
    const g = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.006, 0.15), mat.dark);
    g.position.set(0.003, 0.025 + i * 0.02, 0.125);
    grooves.add(g);
  }
  grooves.rotation.z = 0.42;
  // Cavity back: a dark recess with a thin gold badge
  const back = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.04, 0.13).rotateZ(0.42), mat.dark);
  back.position.set(-0.05, 0.028, 0.125);
  const badge = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.008, 0.07).rotateZ(0.42), mat.accent);
  badge.position.set(-0.052, 0.03, 0.125);
  group.add(body, grooves, back, badge, hosel(mat.steel, 0.024, 0.22));
  return group;
}

function buildPutter(mat) {
  const group = new THREE.Group();
  const W = 0.2, D = 0.11, H = 0.05;   // heel-to-toe, face-to-back, tall

  // Mallet body: flat face in front, a smooth rounded back (a "D" from above)
  const outline = new THREE.Shape();
  outline.moveTo(0, 0);
  outline.lineTo(0, -W);
  outline.bezierCurveTo(-D * 0.75, -W * 1.02, -D, -W * 0.8, -D, -W * 0.5);
  outline.bezierCurveTo(-D, -W * 0.2, -D * 0.75, W * 0.02, 0, 0);
  const bodyGeo = new THREE.ExtrudeGeometry(outline, {
    depth: H, bevelEnabled: true, bevelThickness: 0.008, bevelSize: 0.008, bevelSegments: 1, curveSegments: 8,
  });
  bodyGeo.rotateX(-Math.PI / 2); // extrusion becomes height; outline lies on the turf
  const body = new THREE.Mesh(bodyGeo, mat.graphite);
  body.position.y = 0.012;

  // Bright milled face insert — the part that meets the ball
  const face = new THREE.Mesh(new THREE.BoxGeometry(0.008, H * 0.8, W * 0.86), mat.face);
  face.position.set(0.004, 0.012 + H * 0.5, W * 0.5);

  // One clean sight line down the middle, and a top-line along the face
  const sight = new THREE.Mesh(new THREE.BoxGeometry(D * 0.8, 0.006, 0.012), mat.white);
  sight.position.set(-D * 0.48, 0.012 + H + 0.009, W * 0.5);
  const topLine = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.006, W * 0.9), mat.steel);
  topLine.position.set(-0.008, 0.012 + H + 0.009, W * 0.5);

  // The shaft runs straight down into the heel through a short steel sleeve
  const head = new THREE.Group();
  head.add(body, face, sight, topLine);
  head.position.set(0.035, 0, -0.035);
  const sleeve = hosel(mat.steel, 0.017, 0.24);
  const collar = hosel(mat.white, 0.021, 0.03);
  collar.position.y += 0.2 * Math.cos(LIE);
  collar.position.z -= 0.2 * Math.sin(LIE);
  group.add(collar);
  group.add(head, sleeve);
  return group;
}

export class ClubRig {
  /**
   * lite: a friend's club. The same shapes on cheap shared materials, merged
   * to two draws and casting no shadow; it never fades, so it needs nothing
   * of its own. (Seven friends' clubs at full price were most of a frame.)
   */
  constructor(scene, { lite = false } = {}) {
    this.lite = lite;
    this.group = new THREE.Group();
    this.group.visible = false;
    scene.add(this.group);

    const length = Math.hypot(HANDS_UP, HANDS_IN);
    this.length = length;
    const std = lite
      ? (o) => new THREE.MeshLambertMaterial({ flatShading: true, color: o.color, emissive: o.emissive ?? 0 })
      : (o) => new THREE.MeshStandardMaterial({ flatShading: true, transparent: true, ...o });
    const mat = {
      steel: std({ color: 0xe9eef2, roughness: 0.35, metalness: 0.35, emissive: 0x4a5058 }),
      grip: std({ color: 0x1c1f24, roughness: 0.95 }),
      crown: std({ color: 0x1f3a6e, roughness: 0.3, metalness: 0.3, emissive: 0x0c1730 }),
      face: std({ color: 0xcfd6dc, roughness: 0.5, metalness: 0.3, emissive: 0x3a3f45 }),
      accent: std({ color: 0xffd23f, roughness: 0.5, emissive: 0x5a4300 }),
      dark: std({ color: 0x2a2f36, roughness: 0.8 }),
      graphite: std({ color: 0x3a4250, roughness: 0.45, metalness: 0.35, emissive: 0x161b22 }),
      white: std({ color: 0xffffff, roughness: 0.6, emissive: 0x8a8a8a }),
    };
    this.materials = Object.values(mat);

    // Shaft hangs down -Y from the hands; the head hangs off its tip
    const shaftLen = length - 0.12;
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.015, shaftLen, 6).translate(0, -shaftLen / 2, 0), mat.steel);
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.026, 0.42, 8).translate(0, -0.15, 0), mat.grip);
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.034, 0.034, 0.02, 8).translate(0, 0.065, 0), mat.accent);
    this.wood = buildWood(mat);
    this.iron = buildIron(mat);
    this.putter = buildPutter(mat);
    for (const h of [this.wood, this.iron, this.putter]) {
      // Stand the head up so its sole is flat on the turf at address
      const holder = new THREE.Group();
      holder.position.y = -length;
      holder.rotation.x = LIE;
      holder.add(h);
      this.group.add(holder);
    }
    this.group.add(shaft, handle, cap);
    this.group.traverse((o) => { if (o.isMesh) o.castShadow = !lite; });
    if (lite) {
      // (emissive is per material: fold it into the colour before unifying)
      this.group.traverse((o) => { if (o.isMesh && o.material.emissive) { o.material.color.add(o.material.emissive.clone().multiplyScalar(0.6)); o.material.emissive.setHex(0); } });
      unifyMaterials(this.group);
      bakeStatic(this.group);
      this.materials = [];
    }

    this._f = new THREE.Vector3();
    this._d0 = new THREE.Vector3();
    this._arm = new THREE.Vector3();
    this._q = new THREE.Quaternion();
    // The swing as the golfer's arms see it (read by the camper each frame):
    // the arms turn by armAngle about axis, around the shoulders
    this.axis = new THREE.Vector3(0, 0, 1);
    this.armAngle = 0;
    this._x = new THREE.Vector3();
    this._y = new THREE.Vector3();
    this._z = new THREE.Vector3();
    this._m = new THREE.Matrix4();
    this.opacity = 1;
  }

  /**
   * @param {object} ball  position of the ball
   * @param {number} dirX,dirZ  aim direction
   * @param {number} theta  swing angle (radians)
   * @param {'wood'|'iron'|'putter'} kind
   * @param {number} size   visual scale (matches the drawn ball)
   * @param {number} opacity
   */
  /**
   * theta: the club's angle in the swing plane (0 = address, - back, + through).
   * The swing is two levers: the arms turn about the shoulders (center, when
   * the camper is there to say where that is) by part of theta, and the club
   * hinges at the wrists for the rest, cocked on the way back and released
   * through the ball. A putter has no hinge: arms and club rock together.
   */
  pose(ball, dirX, dirZ, theta, kind, size, opacity, center = null) {
    if (opacity <= 0.01) { this.group.visible = false; return; }
    this.group.visible = true;
    this.wood.visible = kind === 'wood';
    this.iron.visible = kind === 'iron';
    this.putter.visible = kind === 'putter';
    if (opacity !== this.opacity) {
      this.opacity = opacity;
      for (const m of this.materials) m.opacity = opacity;
    }

    const f = this._f.set(dirX, 0, dirZ);
    // The shaft tip (the heel) sits on the turf just behind and inside the ball
    const heel = (kind === 'putter' ? 0.065 : 0.15) * size;
    const tipX = ball.x - dirX * HEAD_BACK * size + dirZ * heel;
    const tipZ = ball.z - dirZ * HEAD_BACK * size - dirX * heel;
    const tipY = ball.y - 0.05;
    // Hands sit up and to the golfer's side (left of the line for a right-hander)
    const px = tipX + dirZ * HANDS_IN * size;
    const pz = tipZ - dirX * HANDS_IN * size;
    const py = tipY + HANDS_UP * size;
    const d0 = this._d0.set(tipX - px, tipY - py, tipZ - pz).normalize();
    // The head travels d(theta) = d0·cos + f·sin: back and up, then through
    const c = Math.cos(theta), s = Math.sin(theta);
    const y = this._y.set(-(d0.x * c + f.x * s), -(d0.y * c + f.y * s), -(d0.z * c + f.z * s));
    const z = this._z.crossVectors(d0, f).normalize();
    const x = this._x.crossVectors(y, z);
    this._m.makeBasis(x, y, z);
    this.group.quaternion.setFromRotationMatrix(this._m);
    // The hands ride round the shoulders on the arms
    const armK = kind === 'putter' ? 1 : theta < 0 ? 0.58 : 0.66;
    this.armAngle = theta * armK;
    this.axis.copy(z);
    const arm = this._arm;
    if (center) arm.set(px - center.x, py - center.y, pz - center.z);
    else arm.copy(d0).multiplyScalar(0.55 * size);
    const cx = px - arm.x, cy = py - arm.y, cz = pz - arm.z;
    arm.applyQuaternion(this._q.setFromAxisAngle(z, this.armAngle));
    this.group.position.set(cx + arm.x, cy + arm.y, cz + arm.z);
    this.group.scale.setScalar(size);
  }

  hide() { this.group.visible = false; }

  /** Where the hands are: the point the club swings about. Same maths as pose(). */
  static pivot(ball, dirX, dirZ, kind, size, out) {
    const heel = (kind === 'putter' ? 0.065 : 0.15) * size;
    const tipX = ball.x - dirX * HEAD_BACK * size + dirZ * heel;
    const tipZ = ball.z - dirZ * HEAD_BACK * size - dirX * heel;
    return out.set(tipX + dirZ * HANDS_IN * size, ball.y - 0.05 + HANDS_UP * size, tipZ - dirX * HANDS_IN * size);
  }
}
